/**
 * The card's outer wrapper, shared by every layout.
 *
 * Two class sets on purpose (see standard.js): core's, which webshop's CSS and
 * both binders key on, plus `.rfhq-pc--<layout>` so the stylesheet can vary the
 * arrangement without any layout needing to know about the others.
 */

import { el, svg } from '../../dom.js';

const TRIALROOM_ICON =
	'<path d="M9 3v6l-6 9a2 2 0 0 0 2 3h14a2 2 0 0 0 2-3l-6-9V3"/>' +
	'<path d="M9 3h6"/>' +
	'<path d="M8.5 12h7"/>';

/**
 * "Try In Trial Room" toggle — a corner overlay independent of the single
 * commerce-action slot (renderAction/card/action/*), which is one kind per
 * card (cart XOR explore XOR ...) and cannot also carry this. Added at the
 * shared root so every layout gets it for free, same "fix once at the base
 * card level" the axis system itself follows. Gated at render time on
 * window.rfhqTrialRoomEnabled (Raftor Storefront Feature Settings) - no flag,
 * no button. Markup + data attribute only; grid.js owns the click.
 */
function trialRoomToggle(model) {
	if (typeof window === 'undefined' || !window.rfhqTrialRoomEnabled) return null;
	if (!model.itemCode) return null;

	return el(
		'button',
		{
			type: 'button',
			class: 'rfhq-pc-trialroom-toggle',
			dataset: { itemCode: model.itemCode },
			'aria-label': 'Try In Trial Room',
			title: 'Try In Trial Room',
		},
		[svg(TRIALROOM_ICON, { class: 'icon sm', viewBox: '0 0 24 24' })]
	);
}

/**
 * @param {object} model
 * @param {object} ctx        render context (columnClass, layout, …)
 * @param {string} layoutName one of standard | compact | minimal | featured
 * @param {Array}  children   the card's inner nodes
 */
export function cardRoot(model, ctx, layoutName, children) {
	const card = el(
		'div',
		{ class: ['card', 'rfhq-card', 'text-left', `rfhq-pc-card--${layoutName}`], dataset: { itemCode: model.itemCode } },
		[trialRoomToggle(model), children]
	);

	// Column width is the grid's business, passed in — a carousel passes ''
	// deliberately, meaning "no bootstrap column, Swiper sizes the slide."
	// `||` treats '' as falsy and silently substitutes 'col-sm-4' back in —
	// found live: every carousel card was getting max-width:33.333% nested
	// inside its own already-narrow slide, compounding down to text wrapping
	// one character per line. Only a genuinely absent value should fall back.
	const columnClass = ctx.columnClass !== undefined ? ctx.columnClass : 'col-sm-4';

	return el(
		'div',
		{
			class: [columnClass, 'item-card', 'rfhq-product-card', `rfhq-pc--${layoutName}`],
			dataset: { itemCode: model.itemCode, cardId: model.id },
		},
		[card]
	);
}
